/**
 * LoginPage — Xyden Leadership Assessment Platform
 * Step 6F Green Phase
 *
 * NFR-F-VAL-001: form fields use React Hook Form + Zod (zodResolver) for
 * per-field, on-blur validation, matching the canonical UserManagementPage
 * pattern. The server-side outcome (invalid credentials / network failure)
 * remains an inline banner — that is a submit result, not a field error.
 */

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { PasswordInput } from "../../components/PasswordInput";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuthStore } from "../../stores/authStore";

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginPageProps {
  onLogin: (credentials: LoginCredentials) => void | Promise<void>;
}

// Field-level schema. Email copy ("Please enter a valid email address.")
// preserves the message the hand-rolled validation previously surfaced.
const loginSchema = z.object({
  email: z
    .string()
    .min(1, "Email is required")
    .email("Please enter a valid email address."),
  password: z.string().min(1, "Password is required"),
});
type LoginForm = z.infer<typeof loginSchema>;

// -- Field error message component (mirrors UserManagementPage)
function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="text-xs text-brand-error mt-1">{message}</p>;
}

export function LoginPage({ onLogin }: LoginPageProps) {
  const [isLoading, setIsLoading] = useState(false);
  // Server/submit error (invalid credentials, network) — distinct from the
  // inline field-validation errors RHF renders under each input.
  const [serverError, setServerError] = useState<string | null>(null);

  // Logout reason banner — shown when the user was forced out (e.g.
  // another login detected). Read once, then clear so it doesn't persist
  // across manual navigations back to /login.
  const logoutReason = useAuthStore((s) => s.logoutReason);
  const clearLogoutReason = useAuthStore((s) => s.clearLogoutReason);
  useEffect(() => {
    return () => { clearLogoutReason(); };
  }, [clearLogoutReason]);

  const form = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
    mode: "onBlur",
  });

  const email = form.watch("email");
  const password = form.watch("password");
  // Preserve the original "Sign In disabled until both fields filled" behavior.
  const isFormComplete = Boolean(email.trim() && password.trim());

  // Clear the server-error banner the moment the user edits, matching the
  // hand-rolled page's "error resets on change" behavior.
  const clearServerError = () => {
    if (serverError) setServerError(null);
  };

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true);
    setServerError(null);
    try {
      await onLogin({ email: data.email, password: data.password });
    } catch (err) {
      setServerError(
        err instanceof Error ? err.message : "Invalid email or password. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-brand-navy">
      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-3">
            <div className="w-10 h-10 rounded-lg bg-brand-crimson flex items-center justify-center">
              <svg
                className="w-6 h-6 text-white"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                <path d="M2 17l10 5 10-5"></path>
                <path d="M2 12l10 5 10-5"></path>
              </svg>
            </div>
            <span className="text-3xl font-bold text-white tracking-tight">XYDEN</span>
          </div>
          <p className="text-slate-400 text-sm">Leadership Maturity Assessment Platform</p>
        </div>

        <div className="bg-white rounded-lg shadow-2xl p-8">
          <div className="mb-6">
            <h1 className="text-xl font-semibold text-slate-900">Welcome back</h1>
            <p className="text-sm text-slate-500 mt-1">Sign in to your account to continue</p>
          </div>

          {logoutReason && (
            <div
              role="status"
              aria-live="polite"
              className="mb-4 px-4 py-3 rounded-lg bg-brand-highlight/40 border border-brand-warning/50 text-amber-800 text-sm font-medium"
            >
              {logoutReason}
            </div>
          )}

          {serverError && (
            <div
              role="alert"
              aria-live="assertive"
              className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm font-medium"
            >
              {serverError}
            </div>
          )}

          <form
            onSubmit={form.handleSubmit(onSubmit)}
            noValidate
            aria-label="Sign in"
            className="space-y-4"
          >
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                aria-label="Email"
                {...form.register("email", { onChange: clearServerError })}
                className={`w-full h-9 rounded-lg border bg-white px-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-crimson/30 focus:border-brand-crimson ${
                  form.formState.errors.email ? "border-brand-error" : "border-brand-steel/50"
                }`}
                placeholder="sara@xyden.com"
              />
              <FieldError message={form.formState.errors.email?.message} />
            </div>

            <div>
              <div className="flex items-baseline justify-between mb-1">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="text-xs font-medium text-brand-crimson hover:underline"
                >
                  Forgot your password?
                </Link>
              </div>
              <PasswordInput
                id="password"
                autoComplete="current-password"
                aria-label="Password"
                {...form.register("password", { onChange: clearServerError })}
                className={`w-full h-9 rounded-lg border bg-white px-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-crimson/30 focus:border-brand-crimson ${
                  form.formState.errors.password ? "border-brand-error" : "border-brand-steel/50"
                }`}
                placeholder="••••••••"
              />
              <FieldError message={form.formState.errors.password?.message} />
            </div>

            <button
              type="submit"
              disabled={!isFormComplete || isLoading}
              className="w-full py-2.5 bg-brand-crimson text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-brand-crimson/90"
            >
              {isLoading ? "Signing in..." : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    </main>
  );
}

export default LoginPage;
